// src/pages/PhotoEditorPage.jsx
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import {
  Box,
  Stack,
  IconButton,
  Paper,
  Slider,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  CircularProgress,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Crop as CropIcon,
  Filter as FilterIcon,
  AutoFixHigh as AutoEnhanceIcon,
  Brush as RemoveBgIcon,
  Download as DownloadIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  RestartAlt as ResetIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Flip as FlipIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import * as bodyPix from '@tensorflow-models/body-pix';
import '@tensorflow/tfjs';
// import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';

// ───────── Presets & Aspect Ratios ─────────
const PRESETS = [
  { name: 'Original', values: { brightness:100, contrast:100, saturate:100, sepia:0, grayscale:0, invert:0, hue:0, blur:0, opacity:100 } },
  { name: 'Vivid',    values: { brightness:108, contrast:118, saturate:145, sepia:0, grayscale:0, invert:0, hue:0, blur:0, opacity:100 } },
  { name: 'Noir',     values: { brightness:100, contrast:150, saturate:0,   sepia:0, grayscale:100,invert:0, hue:0, blur:0, opacity:100 } },
  { name: 'Warm',     values: { brightness:110, contrast:110, saturate:120, sepia:20,grayscale:0,invert:0, hue:12, blur:0, opacity:100 } },
  { name: 'Cool',     values: { brightness:105, contrast:105, saturate:120, sepia:0, grayscale:0,invert:0, hue:-10,blur:0, opacity:100 } },
  { name: 'Vintage',  values: { brightness:95,  contrast:90,  saturate:85,  sepia:35,grayscale:20,invert:0, hue:0, blur:0, opacity:100 } },
  { name: 'Soft',     values: { brightness:110, contrast:92,  saturate:100, sepia:0, grayscale:0,invert:0, hue:0, blur:2, opacity:100 } },
];

const ASPECTS = [
  { label: 'Free', value: null },
  { label: '1:1',  value: 1 },
  { label: '4:3',  value: 4/3 },
  { label: '3:4',  value: 3/4 },
  { label: '16:9', value: 16/9 },
  { label: '21:9', value: 21/9 },
];

// ───────── Helpers ─────────
const loadImage = url =>
  new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => res(img);
    img.onerror = rej;
    img.src     = url;
  });

// simple auto-enhance (histogram stretch)
function autoEnhanceCanvas(canvas) {
  const ctx = canvas.getContext('2d');
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imgData.data;
  let rmin=255, rmax=0, gmin=255, gmax=0, bmin=255, bmax=0;
  // find min/max per channel
  for (let i = 0; i < d.length; i += 4) {
    rmin = Math.min(rmin, d[i]);   rmax = Math.max(rmax, d[i]);
    gmin = Math.min(gmin, d[i+1]); gmax = Math.max(gmax, d[i+1]);
    bmin = Math.min(bmin, d[i+2]); bmax = Math.max(bmax, d[i+2]);
  }
  // stretch
  for (let i = 0; i < d.length; i += 4) {
    d[i]   = ((d[i]   - rmin)/(rmax-rmin))*255;
    d[i+1] = ((d[i+1] - gmin)/(gmax-gmin))*255;
    d[i+2] = ((d[i+2] - bmin)/(bmax-bmin))*255;
  }
  ctx.putImageData(imgData, 0, 0);
}

// ───────── Main Component ─────────
export default function PhotoEditorPage() {
  const theme  = useTheme();
  const smUp   = useMediaQuery(theme.breakpoints.up('sm'));
  const xs     = useMediaQuery(theme.breakpoints.only('xs'));

  // refs
  const inputRef  = useRef();

  // core state
  const [imageSrc,    setImageSrc]    = useState(null);
  const [tool,        setTool]        = useState('crop');       // crop | filter | removeBg | enhance | preview
  const [crop,        setCrop]        = useState({ x:0, y:0 });
  const [zoom,        setZoom]        = useState(1);
  const [rotation,    setRotation]    = useState(0);
  const [aspect,      setAspect]      = useState(null);
  const [pixels,      setPixels]      = useState(null);
  const [filters,     setFilters]     = useState(PRESETS[0].values);
  const [flipH,       setFlipH]       = useState(false);
  const [flipV,       setFlipV]       = useState(false);
  const [previewUrl,  setPreviewUrl]  = useState('');

  // loading / BodyPix
  const [pixModel,       setPixModel]     = useState(null);
  const [modelLoading,   setModelLoading] = useState(true);
  const [processing,     setProcessing]   = useState(false);

  // history
  const [history,    setHistory]    = useState([]);
  const [redoStack,  setRedoStack]  = useState([]);
  const snapshot = () => ({ crop, zoom, rotation, aspect, filters, flipH, flipV });
  const pushHistory = fn => {
    setHistory(h => [...h, snapshot()]);
    fn();
    setRedoStack([]);
  };
  const handleUndo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setRedoStack(r => [snapshot(), ...r]);
    restore(prev);
  };
  const handleRedo = () => {
    if (!redoStack.length) return;
    const next = redoStack[0];
    setRedoStack(r => r.slice(1));
    setHistory(h => [...h, snapshot()]);
    restore(next);
  };
  const restore = s => {
    setCrop(s.crop);
    setZoom(s.zoom);
    setRotation(s.rotation);
    setAspect(s.aspect);
    setFilters(s.filters);
    setFlipH(s.flipH);
    setFlipV(s.flipV);
  };
  const resetAll = () => {
    setImageSrc(null);
    setTool('crop');
    setCrop({ x:0, y:0 });
    setZoom(1);
    setRotation(0);
    setAspect(null);
    setFilters(PRESETS[0].values);
    setFlipH(false);
    setFlipV(false);
    setHistory([]);
    setRedoStack([]);
    setPreviewUrl('');
  };

  // load BodyPix once
  useEffect(() => {
    bodyPix.load({ multiplier: 0.75 }).then(m => {
      setPixModel(m);
      setModelLoading(false);
    });
  }, []);

  // upload handler
  const handleFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    resetAll();
    setImageSrc(URL.createObjectURL(f));
  };

  // CSS filter
  const cssFilter = () => `
    brightness(${filters.brightness}%)
    contrast(${filters.contrast}%)
    saturate(${filters.saturate}%)
    sepia(${filters.sepia}%)
    grayscale(${filters.grayscale}%)
    invert(${filters.invert}%)
    hue-rotate(${filters.hue}deg)
    blur(${filters.blur}px)
    opacity(${filters.opacity}%)
  `;
  const overlayStyle = {
    filter: cssFilter(),
    transform: `scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`
  };

  // apply crop & filters → preview
  const applyChanges = useCallback(async () => {
    if (!imageSrc || !pixels) return;
    setProcessing(true);
    try {
      const img = await loadImage(imageSrc);
      const c = document.createElement('canvas');
      c.width = pixels.width;
      c.height = pixels.height;
      const ctx = c.getContext('2d');
      ctx.filter = cssFilter();
      ctx.save();
      ctx.translate(c.width/2, c.height/2);
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-c.width/2, -c.height/2);
      ctx.drawImage(
        img,
        pixels.x, pixels.y, pixels.width, pixels.height,
        0, 0, pixels.width, pixels.height
      );
      ctx.restore();
      setPreviewUrl(c.toDataURL('image/png'));
      setTool('preview');
    } catch (err) {
      console.error(err);
      alert('Error applying edits');
    }
    setProcessing(false);
  }, [imageSrc, pixels, filters, rotation, flipH, flipV]);

  // background removal
const removeBackground = useCallback(async () => {
  if (!pixModel) return;
  const srcUrl = previewUrl || imageSrc;
  if (!srcUrl) return;

  setProcessing(true);
  try {
    // 1) load the image
    const img = await loadImage(srcUrl);

    // 2) run segmentation
    const segmentation = await pixModel.segmentPerson(img);
    const maskData = segmentation.data; // Uint8Array of 0|1

    // 3) draw original into a canvas
    const c = document.createElement('canvas');
    c.width = img.width;
    c.height = img.height;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0);

    // 4) pull out pixel data
    const imageData = ctx.getImageData(0, 0, c.width, c.height);
    const d = imageData.data;

    // 5) zero-out alpha on background pixels
    for (let i = 0; i < maskData.length; i++) {
      if (maskData[i] === 0) {
        d[i*4 + 3] = 0;
      }
    }

    // 6) paint it back
    ctx.putImageData(imageData, 0, 0);

    // 7) update preview
    setPreviewUrl(c.toDataURL('image/png'));
    setTool('preview');
  } catch (err) {
    console.error(err);
    alert('Background removal failed');
  }
  setProcessing(false);
}, [pixModel, previewUrl, imageSrc]);


  // auto-enhance
  const autoEnhance = async () => {
    if (!previewUrl) return;
    setProcessing(true);
    try {
      const img = await loadImage(previewUrl);
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0);
      autoEnhanceCanvas(c);
      setPreviewUrl(c.toDataURL('image/png'));
      setTool('preview');
    } catch (err) {
      console.error(err);
      alert('Auto-enhance failed');
    }
    setProcessing(false);
  };

  // download
  const downloadImage = () => {
    if (!previewUrl) {
      alert('Apply edits first');
      return;
    }
    const a = document.createElement('a');
    a.download = 'edited.png';
    a.href = previewUrl;
    a.click();
  };

  // IconButton + Tooltip
  const ToolBtn = ({ title, icon, onClick, color, disabled }) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          color={color}
          onClick={onClick}
          disabled={disabled || processing || modelLoading}
          size={xs ? 'large' : 'medium'}
        >{icon}</IconButton>
      </span>
    </Tooltip>
  );

  // toolbar
  const toolbar = (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />
      <ToolBtn
        title="Upload"
        icon={<UploadIcon />}
        onClick={() => inputRef.current.click()}
      />
      <ToolBtn
        title="Crop"
        icon={<CropIcon />}
        onClick={() => setTool('crop')}
        color={tool==='crop'?'primary':'default'}
        disabled={!imageSrc}
      />
      <ToolBtn
        title="Filters"
        icon={<FilterIcon />}
        onClick={() => setTool('filter')}
        color={tool==='filter'?'primary':'default'}
        disabled={!imageSrc}
      />
      <ToolBtn
        title="Remove BG"
        icon={<RemoveBgIcon />}
        onClick={() => setTool('removeBg')}
        color={tool==='removeBg'?'primary':'default'}
        disabled={(!previewUrl && !imageSrc) || modelLoading}
      />
      <ToolBtn
        title="Enhance"
        icon={<AutoEnhanceIcon />}
        onClick={() => setTool('enhance')}
        color={tool==='enhance'?'primary':'default'}
        disabled={!previewUrl}
      />
      <ToolBtn
        title="Undo"
        icon={<UndoIcon />}
        onClick={handleUndo}
        disabled={!history.length}
      />
      <ToolBtn
        title="Redo"
        icon={<RedoIcon />}
        onClick={handleRedo}
        disabled={!redoStack.length}
      />
      <ToolBtn
        title="Reset"
        icon={<ResetIcon />}
        onClick={resetAll}
      />
      <ToolBtn
        title="Download"
        icon={<DownloadIcon />}
        onClick={downloadImage}
        disabled={!previewUrl}
      />
    </>
  );

  const [drawerOpen, setDrawerOpen] = useState(false);

  // ───────── Controls ─────────
  function CropControls() {
    return (
      <>
        <Typography gutterBottom>Aspect Ratio</Typography>
        <ToggleButtonGroup
          value={aspect}
          exclusive
          fullWidth
          size="small"
          onChange={(_,v)=>pushHistory(()=>setAspect(v))}
          sx={{ mb:2 }}
        >
          {ASPECTS.map(a=>(
            <ToggleButton key={a.label} value={a.value}>{a.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography gutterBottom>Zoom {zoom.toFixed(2)}×</Typography>
        <Slider
          value={zoom}
          min={1} max={3} step={0.01}
          onChange={(_,v)=>pushHistory(()=>setZoom(v))}
          sx={{ mb:2 }}
        />

        <Typography gutterBottom>Rotate {rotation}°</Typography>
        <Slider
          value={rotation}
          min={0} max={360}
          onChange={(_,v)=>pushHistory(()=>setRotation(v))}
          sx={{ mb:2 }}
        />

        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mb:2 }}>
          <IconButton size="small" onClick={()=>pushHistory(()=>setRotation(r=>(r+90)%360))}>
            <RotateRightIcon/>
          </IconButton>
          <IconButton size="small" onClick={()=>pushHistory(()=>setRotation(r=>(r+270)%360))}>
            <RotateLeftIcon/>
          </IconButton>
        </Stack>

        <Button
          variant="contained"
          fullWidth
          onClick={applyChanges}
          disabled={processing}
        >
          Apply Crop & Filters
        </Button>
      </>
    );
  }

  function FilterControls() {
    return (
      <>
        <Typography gutterBottom>Flip</Typography>
        <Stack direction="row" spacing={1} sx={{ mb:2 }}>
          <Button
            variant={flipH?'contained':'outlined'}
            size="small"
            startIcon={<FlipIcon sx={{ transform:'scaleX(-1)' }}/>}
            onClick={()=>pushHistory(()=>setFlipH(h=>!h))}
          >H</Button>
          <Button
            variant={flipV?'contained':'outlined'}
            size="small"
            startIcon={<FlipIcon sx={{ transform:'scaleY(-1)' }}/>}
            onClick={()=>pushHistory(()=>setFlipV(v=>!v))}
          >V</Button>
        </Stack>

        <Typography gutterBottom>Presets</Typography>
        <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mb:2 }}>
          {PRESETS.map(p=>(
            <Button
              key={p.name}
              size="small"
              variant="outlined"
              onClick={()=>pushHistory(()=>setFilters(p.values))}
            >{p.name}</Button>
          ))}
        </Stack>

        {[
          { k:'brightness', l:'Brightness', min:0, max:200, unit:'%' },
          { k:'contrast',   l:'Contrast',   min:0, max:200, unit:'%' },
          { k:'saturate',   l:'Saturation', min:0, max:200, unit:'%' },
          { k:'sepia',      l:'Sepia',      min:0, max:100, unit:'%' },
          { k:'grayscale',  l:'Grayscale',  min:0, max:100, unit:'%' },
          { k:'invert',     l:'Invert',     min:0, max:100, unit:'%' },
          { k:'hue',        l:'Hue',        min:-180, max:180, unit:'°' },
          { k:'blur',       l:'Blur',       min:0, max:20,  unit:'px' },
          { k:'opacity',    l:'Opacity',    min:0, max:100, unit:'%' },
        ].map(cfg=>(
          <Box key={cfg.k} sx={{ mb:2 }}>
            <Typography gutterBottom>
              {cfg.l}: {filters[cfg.k]}{cfg.unit}
            </Typography>
            <Slider
              value={filters[cfg.k]}
              min={cfg.min}
              max={cfg.max}
              onChange={(_,v)=>pushHistory(()=>setFilters(f=>({...f,[cfg.k]:v})))}
            />
          </Box>
        ))}

        <Button
          variant="contained"
          fullWidth
          onClick={applyChanges}
          disabled={processing}
          sx={{ mb:1 }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={()=>pushHistory(()=>setFilters(PRESETS[0].values))}
        >
          Reset Filters
        </Button>
      </>
    );
  }

  function RemoveBgControls() {
    return (
      <Button
        variant="contained"
        fullWidth
        onClick={removeBackground}
        disabled={processing||modelLoading}
      >
        {modelLoading
          ? 'Loading Model…'
          : processing
            ? 'Removing…'
            : 'Apply Remove Background'
        }
      </Button>
    );
  }

  function EnhanceControls() {
    return (
      <Button
        variant="contained"
        fullWidth
        onClick={autoEnhance}
        disabled={processing}
      >
        {processing ? 'Enhancing…' : 'Apply Auto-Enhance'}
      </Button>
    );
  }

  return (
    <>
      {/* <Navbar/> */}
      <Box sx={{
        display:'grid',
        gridTemplateColumns:{ xs:'1fr', sm:'64px 1fr 300px' },
        gridTemplateRows:{ xs:'1fr auto', sm:'1fr' },
        height:{ xs:'calc(100vh-56px)', sm:'calc(100vh-64px)' },
      }}>
        {/* desktop toolbar */}
        {smUp && (
          <Stack
            p={1}
            spacing={1}
            bgcolor="background.paper"
            borderRight={`1px solid ${theme.palette.divider}`}
          >
            {toolbar}
          </Stack>
        )}

        {/* workspace */}
        <Box sx={{
          position:'relative',
          bgcolor:'#f4f4f4',
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
        }}>
          {/* cropping & live filter overlay */}
          {imageSrc && (tool!=='preview') && (
            <Box sx={{ position:'absolute', inset:0, ...overlayStyle }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect||undefined}
                onCropChange={c=>pushHistory(()=>setCrop(c))}
                onZoomChange={z=>pushHistory(()=>setZoom(z))}
                onRotationChange={r=>pushHistory(()=>setRotation(r))}
                onCropComplete={(_,px)=>setPixels(px)}
              />
            </Box>
          )}

          {/* preview image */}
          {previewUrl && tool==='preview' && (
            <img
              src={previewUrl}
              alt="Preview"
              style={{ maxWidth:'100%', maxHeight:'100%', background:'#fff' }}
            />
          )}

          {/* placeholder */}
          {!imageSrc && (
            <Typography color="text.secondary">
              Upload an image to begin
            </Typography>
          )}

          {/* global spinner */}
          {processing && (
            <Box sx={{
              position:'absolute',
              inset:0,
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              backdropFilter:'blur(2px)',
            }}>
              <CircularProgress size={64}/>
            </Box>
          )}
        </Box>

        {/* desktop controls panel */}
        {smUp && (
          <Paper square elevation={3} sx={{ p:2, overflowY:'auto' }}>
            {{
              crop:      <CropControls/>,
              filter:    <FilterControls/>,
              removeBg:  <RemoveBgControls/>,
              enhance:   <EnhanceControls/>,
              preview:   <Typography align="center">Download when ready ↓</Typography>,
            }[tool]}
          </Paper>
        )}
      </Box>

      {/* mobile bottom bar */}
      {xs && (
        <Paper square elevation={6} sx={{
          position:'fixed',
          bottom:0,
          left:0,
          right:0,
          display:'flex',
          overflowX:'auto',
          p:1,
          gap:1,
        }}>
          {toolbar}
          <IconButton onClick={()=>setDrawerOpen(true)}>
            <SettingsIcon/>
          </IconButton>
        </Paper>
      )}

      {/* mobile drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onOpen={()=>setDrawerOpen(true)}
        onClose={()=>setDrawerOpen(false)}
      >
        <Box sx={{ p:2, height:'60vh', overflowY:'auto' }}>
          {{
            crop:      <CropControls/>,
            filter:    <FilterControls/>,
            removeBg:  <RemoveBgControls/>,
            enhance:   <EnhanceControls/>,
            preview:   <Typography align="center">Download when ready ↓</Typography>,
          }[tool]}
          <Button fullWidth sx={{ mt:2 }} onClick={()=>setDrawerOpen(false)}>
            Close
          </Button>
        </Box>
      </SwipeableDrawer>

      {/* <Footer/> */}
    </>
  );
}
